import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
  type FaqItem,
  type RelatedLink,
} from "@/components/SeoLandingPage";
import limoCard from "@/assets/sprinter-limo-2.jpg?format=webp&quality=70&w=800&imagetools";
import execCard from "@/assets/sprinter-exec-green.jpg?format=webp&quality=70&w=800&imagetools";
import jetCard from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=70&w=800&imagetools";
import suburbanCard from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=70&w=800&imagetools";
import ratesCard from "@/assets/sprinter-limo-hotel-arrival-atlanta.jpg?format=webp&quality=70&w=800&imagetools";

/** Shared config for one-vehicle showcase pages (Jet, Executive, Suburban). */
export type VehicleConfig = {
  path: string;
  title: string;
  description: string;
  serviceType: string;
  eyebrow: string;
  h1: string;
  intro: string;
  heroImage: string;
  heroAlt: string;
  midImage: string;
  midAlt: string;
  aboutTitle: string;
  about: string[];
  featuresTitle: string;
  features: string[];
  bestFor: string[];
  gallery: { src: string; alt: string; caption?: string }[];
  faqs: FaqItem[];
  related: RelatedLink[];
  ctaTitle: string;
  ctaBody: string;
  quickFacts: { value: string; label: string }[];
  introImage: string;
  introImageAlt: string;
  faqImage?: string;
  faqImageAlt?: string;
  details: { title: string; body?: string[]; list?: string[]; image?: string; imageAlt?: string }[];
  bookingBands?: { title: string; body: string }[];
};

const SITE = "https://atlantaluxurysprinters.com";

export function vehicleHead(cfg: VehicleConfig) {
  const url = `${SITE}${cfg.path}`;
  return {
    meta: [
      { title: cfg.title },
      { name: "description", content: cfg.description },
      { property: "og:title", content: cfg.title },
      { property: "og:description", content: cfg.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: cfg.title },
      { name: "twitter:description", content: cfg.description },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url,
            name: `Atlanta Luxury Sprinters, ${cfg.serviceType}`,
            image: cfg.heroImage,
            description: cfg.description,
            serviceType: cfg.serviceType,
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Midtown Atlanta" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
            ],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Fleet", item: `${SITE}/rentals` },
            { "@type": "ListItem", position: 3, name: cfg.h1, item: url },
          ],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(cfg.faqs)) },
    ],
  };
}

export default function VehiclePage({ cfg }: { cfg: VehicleConfig }) {
  return (
    <SeoLandingPage
      rich
      eyebrow={cfg.eyebrow}
      h1={cfg.h1}
      intro={cfg.intro}
      heroImage={cfg.heroImage}
      heroAlt={cfg.heroAlt}
      quickFacts={cfg.quickFacts}
      introImage={cfg.introImage}
      introImageAlt={cfg.introImageAlt}
      midImage={cfg.midImage}
      midAlt={cfg.midAlt}
      sectionOneTitle={cfg.aboutTitle}
      sectionOneBody={cfg.about}
      bulletsTitle={cfg.featuresTitle}
      bulletsIntro="What's inside, straight from our fleet."
      bullets={cfg.features}
      bookingBands={cfg.bookingBands}
      coverageEyebrow="Best For"
      coverageTitle="What Clients Book It For"
      coverageIntro="The trips clients book this vehicle for most."
      coverage={cfg.bestFor}
      detailSections={cfg.details}
      gallery={{ title: "Real Photos of This Vehicle", intro: "Actual photos from our Atlanta fleet. We own every vehicle in our fleet.", images: cfg.gallery }}
      faqTitle="Questions About This Vehicle"
      faqs={cfg.faqs}
      faqImage={cfg.faqImage}
      faqImageAlt={cfg.faqImageAlt}
      relatedTitle="Compare Our Other Vehicles"
      related={cfg.related}
      ctaTitle={cfg.ctaTitle}
      ctaBody={cfg.ctaBody}
    />
  );
}

/** Which vehicle fits: shared, factual capacity guide used on every vehicle page. */
export const WHICH_VEHICLE = {
  title: "Which Vehicle Fits Your Group?",
  body: ["Not sure this is the right one? Here is how the fleet compares. Text me your group size and plans, and I'll recommend the right fit with your quote in writing."],
  list: [
    "Up to 14, celebrating together: Limo Style Sprinter, with wraparound lounge seating and a private bar.",
    "Up to 13 who want individual seats: Executive Sprinter, with conference seating and power at every seat.",
    "Up to 7 in first-class comfort: Jet Sprinter, with captain seats, a starlight ceiling and a cinema screen.",
    "Up to 6 with captain seats or 7 with a bench: Suburban SUV, discreet, with generous luggage space.",
  ],
};

export const FLEET_LINKS: Record<string, RelatedLink> = {
  limo: { to: "/sprinter-limo-atlanta", title: "Limo Style Sprinter", desc: "Up to 14 guests, LED ceiling, private bar, lounge leather seating.", image: limoCard },
  executive: { to: "/executive-sprinter-atlanta", title: "Executive Sprinter", desc: "Up to 13 guests, conference seating, two TVs, power at every seat.", image: execCard },
  jet: { to: "/jet-sprinter-atlanta", title: "Jet Sprinter", desc: "Up to 7 guests, starlight ceiling, first-class captain seats, cinema screen.", image: jetCard },
  suburban: { to: "/suburban-chauffeur-service-atlanta", title: "Suburban SUV", desc: "Up to 6 with captain seats or 7 with a bench, discreet and private.", image: suburbanCard },
  rates: { to: "/private-chauffeur-hourly-rates-atlanta", title: "Hourly Rates", desc: "Sprinters from $250/hour. Custom quotes upon request.", image: ratesCard },
};
